import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assessmentService } from '../services/assessmentService';
import { consultationService } from '../services/consultationService';
import { calendarService } from '../services/calendarService';
import { workflowService } from '../services/workflowService';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';

import { useEmailNotifications } from '../hooks/useEmailNotifications';

const Dashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const navigate = useNavigate();
  const { toastNotification, dismissToast } = useEmailNotifications();

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  const loadUserData = async () => {
    try {
      const [assessmentsData, consultationsData, bookingsData, tasksData] = await Promise.all([
        assessmentService?.getUserAssessments(user?.id),
        consultationService?.getUserConsultations(user?.id),
        calendarService?.getUserBookings(user?.id),
        workflowService?.getMyTasks()
      ]);
      setAssessments(assessmentsData || []);
      setConsultations(consultationsData || []);
      setBookings(bookingsData || []);
      setMyTasks(tasksData || []);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAssessmentTypeLabel = (type) => {
    const labels = {
      'roi_calculator': 'ROI Calculator',
      'data_maturity': 'Data Maturity Assessment'
    };
    return labels?.[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'scheduled': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }
    
    try {
      await calendarService?.cancelBooking(bookingId);
      await loadUserData();
    } catch (error) {
      console.error('Error canceling booking:', error);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      await workflowService?.updateAutomatedTask(taskId, { status: newStatus });
      await loadUserData();
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800 border-red-200',
      'high': 'bg-orange-100 text-orange-800 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'low': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in_progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
      <Header />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Section */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Welcome back, {userProfile?.full_name || 'User'}!
                </h1>
                <p className="text-muted-foreground">
                  {userProfile?.email}
                  {userProfile?.company && ` • ${userProfile?.company}`}
                </p>
              </div>
              <div className="mt-4 md:mt-0">
                <Button
                  variant="outline"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-elevation-1 p-6 hover:shadow-elevation-2 hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center hover:bg-primary/20 transition-colors duration-300">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
                <span className="text-3xl font-bold text-foreground">{assessments?.length}</span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Total Assessments</h3>
            </div>

            <div className="bg-white rounded-xl shadow-elevation-1 p-6 hover:shadow-elevation-2 hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center hover:bg-secondary/20 transition-colors duration-300">
                  <Icon name="Calendar" size={24} className="text-secondary" />
                </div>
                <span className="text-3xl font-bold text-foreground">{bookings?.length}</span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Scheduled Consultations</h3>
            </div>

            <div className="bg-white rounded-xl shadow-elevation-1 p-6 hover:shadow-elevation-2 hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center hover:bg-accent/20 transition-colors duration-300">
                  <Icon name="MessageSquare" size={24} className="text-accent" />
                </div>
                <span className="text-3xl font-bold text-foreground">{consultations?.length}</span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Consultations</h3>
            </div>

            <div className="bg-white rounded-xl shadow-elevation-1 p-6 hover:shadow-elevation-2 hover:scale-105 transition-all duration-300 cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center hover:bg-teal-200 transition-colors duration-300">
                  <Icon name="CheckSquare" size={24} className="text-teal-600" />
                </div>
                <span className="text-3xl font-bold text-foreground">
                  {myTasks?.filter(t => t?.status !== 'completed')?.length}
                </span>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Pending Tasks</h3>
            </div>
          </div>

          {/* My Tasks Section */}
          {myTasks?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <Icon name="CheckSquare" size={28} className="text-primary" />
                  My Tasks
                </h2>
              </div>
              <div className="space-y-4">
                {myTasks?.slice(0, 5)?.map((task) => (
                  <div
                    key={task?.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-secondary/50 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">{task?.title}</h3>
                        {task?.description && (
                          <p className="text-sm text-muted-foreground mb-2">{task?.description}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {task?.clientName && (
                            <span className="flex items-center gap-1">
                              <Icon name="User" size={14} />
                              {task?.clientName}
                            </span>
                          )}
                          {task?.serviceInterest && (
                            <span className="flex items-center gap-1">
                              <Icon name="Briefcase" size={14} />
                              {task?.serviceInterest}
                            </span>
                          )}
                          {task?.dueDate && (
                            <span className="flex items-center gap-1">
                              <Icon name="Clock" size={14} />
                              Due: {formatDate(task?.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task?.priority)}`}>
                          {task?.priority}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTaskStatusColor(task?.status)}`}>
                          {task?.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {task?.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTaskStatusUpdate(task?.id, 'in_progress')}
                        >
                          Start Task
                        </Button>
                      )}
                      {task?.status === 'in_progress' && (
                        <Button
                          size="sm"
                          onClick={() => handleTaskStatusUpdate(task?.id, 'completed')}
                        >
                          Mark Complete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Assessments */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground flex items-center">
                <Icon name="FileText" size={28} className="mr-3 text-primary" />
                Recent Assessments
              </h2>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/roi-calculator-suite-value-demonstration-tools')}
              >
                New Assessment
              </Button>
            </div>

            {assessments?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No assessments yet</p>
                <Button
                  variant="default"
                  iconName="Calculator"
                  iconPosition="left"
                  onClick={() => navigate('/data-maturity-calculator-lead-generation-assessment')}
                >
                  Start Your First Assessment
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {assessments?.slice(0, 5)?.map((assessment) => (
                  <div
                    key={assessment?.id}
                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">{assessment?.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <span className="flex items-center">
                            <Icon name="Tag" size={14} className="mr-1" />
                            {getAssessmentTypeLabel(assessment?.assessment_type)}
                          </span>
                          <span className="flex items-center">
                            <Icon name="Calendar" size={14} className="mr-1" />
                            {formatDate(assessment?.created_at)}
                          </span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" iconName="Eye">
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Consultations */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground flex items-center">
                <Icon name="Calendar" size={28} className="mr-3 text-secondary" />
                Scheduled Consultations
              </h2>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/contact-multi-channel-engagement-hub')}
              >
                Book Consultation
              </Button>
            </div>

            {bookings?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="CalendarX" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No consultations scheduled</p>
                <Button
                  variant="default"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => navigate('/contact-multi-channel-engagement-hub')}
                >
                  Schedule a Consultation
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings?.map((booking) => (
                  <div
                    key={booking?.id}
                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-foreground">{booking?.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking?.status)}`}>
                            {booking?.status}
                          </span>
                        </div>
                        {booking?.description && (
                          <p className="text-sm text-muted-foreground mb-3">{booking?.description}</p>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Icon name="Calendar" size={14} className="mr-2" />
                            {formatDate(booking?.scheduledStart)}
                          </div>
                          <div className="flex items-center">
                            <Icon name="Clock" size={14} className="mr-2" />
                            {new Date(booking?.scheduledStart)?.toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            })}
                          </div>
                          {booking?.serviceInterest && (
                            <div className="flex items-center">
                              <Icon name="Briefcase" size={14} className="mr-2" />
                              {booking?.serviceInterest}
                            </div>
                          )}
                          <div className="flex items-center">
                            <Icon name="Timer" size={14} className="mr-2" />
                            {booking?.durationMinutes} minutes
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2 ml-4">
                        {booking?.status === 'pending' || booking?.status === 'confirmed' ? (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            iconName="X"
                            onClick={() => handleCancelBooking(booking?.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Legacy Consultations (from old system) */}
          {consultations?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center">
                  <Icon name="FileText" size={28} className="mr-3 text-secondary" />
                  Previous Consultations
                </h2>
              </div>
              <div className="space-y-4">
                {consultations?.map((consultation) => (
                  <div
                    key={consultation?.id}
                    className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-1">{consultation?.title}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{consultation?.description}</p>
                        <div className="flex items-center space-x-4 text-sm">
                          {consultation?.scheduled_date && (
                            <span className="flex items-center text-muted-foreground">
                              <Icon name="Clock" size={14} className="mr-1" />
                              {formatDate(consultation?.scheduled_date)}
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(consultation?.status)}`}>
                            {consultation?.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;